/* De offertelijst (/crm/quotes): één compacte, doorzoekbare lijst, zoals de projectlijst.
 *
 *   /           zoeken
 *   ↑ ↓ (j k)   door de lijst
 *   Enter       offerte openen (cmd-klik op een rij: nieuw tabblad)
 *
 * Statustabs tellen aantal en waarde binnen je zoekterm. Per offerte: nummer, titel,
 * project of klant, bedrag, status, hoe lang hij open staat, en of hij verloopt of
 * verlopen is. Verlopen is afgeleid, nooit opgeslagen (ADR 0018). */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, LayoutTemplate, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  filterEnSorteer, tellingen, traject, TAB_LABEL, type LijstFilter, type LijstOfferte, type Sortering, type Tab,
} from './data/lijst';
import { duur, lokaleDag, openSinds, signaal, weergave } from './data/status';
import { datum, euro } from '../projectpagina/ui/opmaak';
import { StatusLabel, SignaalLabel } from './ui/StatusLabel';

const EURO = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const euroKort = (v: number) =>
  v >= 1_000_000 ? `€ ${(v / 1_000_000).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} mln` : v >= 1000 ? `€ ${Math.round(v / 1000)}k` : v ? EURO.format(v) : '';

const STIP: Partial<Record<Tab, string>> = {
  concept: 'bg-slate-400', verstuurd: 'bg-blue-600', verlopen: 'bg-red-600', geaccepteerd: 'bg-green-600', afgewezen: 'bg-zinc-400',
};

const KOLOMMEN =
  'grid-cols-[64px_minmax(0,1fr)] sm:grid-cols-[72px_minmax(0,1fr)_112px] md:grid-cols-[76px_minmax(0,1fr)_112px_104px] lg:grid-cols-[80px_minmax(0,1fr)_120px_110px_84px_150px]';

interface Props {
  offertes: LijstOfferte[];
  laden: boolean;
  fout: Error | null;
  filter: LijstFilter;
  onFilter: (f: LijstFilter) => void;
  onNieuw: () => void;
}

function Meta({ q }: { q: LijstOfferte }) {
  const t = traject(q);
  const delen = [
    t?.label,
    q.organization?.name && !t?.label.includes(q.organization.name) ? q.organization.name : null,
    q.status === 'sent' && q.sent_to ? `aan ${q.sent_to}` : null,
  ].filter(Boolean);
  if (!delen.length) return null;
  return <span className="truncate text-xs text-muted-foreground">{delen.join(' · ')}</span>;
}

export function OfferteLijst({ offertes, laden, fout, filter, onFilter, onNieuw }: Props) {
  const navigate = useNavigate();
  const zoekRef = useRef<HTMLInputElement>(null);
  const rijRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [actief, setActief] = useState(-1);
  const vandaag = lokaleDag();

  const zichtbaar = useMemo(() => filterEnSorteer(offertes, filter, vandaag), [offertes, filter, vandaag]);
  const tabs = useMemo(() => tellingen(offertes, filter.zoek, vandaag), [offertes, filter.zoek, vandaag]);

  useEffect(() => setActief(-1), [filter.zoek, filter.tab]);
  useEffect(() => {
    if (actief >= 0) rijRefs.current[actief]?.scrollIntoView({ block: 'nearest' });
  }, [actief]);

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement;
      const typen = doel.tagName === 'INPUT' || doel.tagName === 'TEXTAREA' || doel.isContentEditable;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!typen && e.key === '/') {
        e.preventDefault();
        zoekRef.current?.focus();
        return;
      }
      if (typen && doel !== zoekRef.current) return;
      if (e.key === 'ArrowDown' || (!typen && e.key === 'j')) {
        e.preventDefault();
        if (typen) zoekRef.current?.blur();
        setActief((a) => Math.min(zichtbaar.length - 1, a + 1));
      } else if (e.key === 'ArrowUp' || (!typen && e.key === 'k')) {
        e.preventDefault();
        setActief((a) => Math.max(0, a - 1));
      } else if (e.key === 'Enter' && zichtbaar.length) {
        const q = zichtbaar[actief >= 0 ? actief : 0];
        if (q && (typen || actief >= 0)) {
          e.preventDefault();
          navigate(`/crm/quotes/${q.id}`);
        }
      } else if (e.key === 'Escape' && typen) {
        onFilter({ ...filter, zoek: '' });
        zoekRef.current?.blur();
      }
    };
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [zichtbaar, actief, filter, onFilter, navigate]);

  const sorteer = (s: Sortering) =>
    onFilter({ ...filter, sortering: s, oplopend: filter.sortering === s ? !filter.oplopend : s === 'titel' || s === 'open' || s === 'geldig' });

  const Kop = ({ s, children, rechts }: { s: Sortering; children: React.ReactNode; rechts?: boolean }) => (
    <button
      type="button"
      onClick={() => sorteer(s)}
      className={cn('inline-flex items-center gap-1 hover:text-foreground', rechts && 'justify-end', filter.sortering === s && 'font-semibold text-foreground')}
    >
      {children}
      {filter.sortering === s && (filter.oplopend ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Werkbalk */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <h1 className="mr-2 text-2xl font-semibold tracking-tight">Offertes</h1>
        <label className="relative order-last w-full sm:order-none sm:ml-auto sm:w-72">
          <span className="sr-only">Zoek offerte</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            ref={zoekRef}
            value={filter.zoek}
            onChange={(e) => onFilter({ ...filter, zoek: e.target.value })}
            placeholder="Zoek offerte, project of klant"
            className="h-9 pl-8 pr-8"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border bg-muted px-1.5 font-mono text-[11px] text-muted-foreground sm:block">/</kbd>
        </label>
        <Button variant="outline" size="sm" className="ml-auto h-9 sm:ml-0" asChild>
          <Link to="/crm/quote-templates">
            <LayoutTemplate className="mr-1.5 h-4 w-4" />
            <span className="hidden md:inline">Sjablonen</span>
          </Link>
        </Button>
        <Button size="sm" onClick={onNieuw} className="h-9">
          <Plus className="mr-1.5 h-4 w-4" />
          <span className="hidden sm:inline">Nieuwe offerte</span>
          <span className="sm:hidden">Nieuw</span>
        </Button>
      </div>

      {/* Statustabs */}
      <div role="tablist" aria-label="Status" className="-mx-3 flex w-[calc(100%+1.5rem)] gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
        {tabs.map((t) => {
          const aan = filter.tab === t.tab;
          return (
            <button
              key={t.tab}
              type="button"
              role="tab"
              aria-selected={aan}
              onClick={() => onFilter({ ...filter, tab: t.tab })}
              className={cn(
                'flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
                aan ? 'border-foreground bg-foreground text-background' : 'bg-background hover:bg-muted',
                !aan && t.tab === 'verlopen' && t.aantal > 0 && 'border-red-200 text-red-800',
              )}
            >
              {STIP[t.tab] && <span className={cn('h-2 w-2 rounded-full', STIP[t.tab])} aria-hidden />}
              <span className="font-medium">{TAB_LABEL[t.tab]}</span>
              <span className={cn('tabular-nums', aan ? 'opacity-80' : 'text-muted-foreground')}>{t.aantal}</span>
              {t.waarde > 0 && <span className={cn('hidden tabular-nums 2xl:inline', aan ? 'opacity-70' : 'text-muted-foreground')}>· {euroKort(t.waarde)}</span>}
            </button>
          );
        })}
      </div>

      {fout ? (
        <div className="rounded-xl border border-destructive/40 px-5 py-6 text-sm">
          <p className="font-medium text-destructive">Offertes konden niet geladen worden.</p>
          <p className="text-muted-foreground">{fout.message}</p>
        </div>
      ) : laden ? (
        <div className="flex flex-col gap-2 rounded-xl border p-4" aria-busy>
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-9 animate-pulse rounded bg-muted" />)}
        </div>
      ) : zichtbaar.length === 0 ? (
        <div className="rounded-xl border px-5 py-10 text-center text-sm text-muted-foreground">
          {offertes.length === 0 ? 'Nog geen offertes.' : 'Geen offertes gevonden.'}{' '}
          {(filter.zoek || filter.tab !== 'alle') && offertes.length > 0 && (
            <button type="button" className="font-medium text-foreground underline" onClick={() => onFilter({ ...filter, zoek: '', tab: 'alle' })}>
              Toon alles
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-background">
          <div className={cn('grid items-center gap-3 border-b bg-muted/40 px-4 py-2 text-xs text-muted-foreground', KOLOMMEN)}>
            <Kop s="nummer">Nr</Kop>
            <Kop s="titel">Offerte</Kop>
            <span className="hidden sm:block">Status</span>
            <span className="hidden text-right md:block"><Kop s="bedrag" rechts>Bedrag</Kop></span>
            <span className="hidden text-right lg:block"><Kop s="open" rechts>Open sinds</Kop></span>
            <span className="hidden lg:block"><Kop s="geldig">Geldig tot</Kop></span>
          </div>
          <ul className="divide-y">
            {zichtbaar.map((q, i) => {
              const w = weergave(q, vandaag);
              const sig = signaal(q, vandaag);
              const sinds = openSinds(q);
              return (
                <li key={q.id} className={cn(i === actief && 'bg-muted')}>
                  <Link
                    ref={(el) => (rijRefs.current[i] = el)}
                    to={`/crm/quotes/${q.id}`}
                    onMouseEnter={() => setActief(i)}
                    className={cn('grid items-center gap-3 px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', KOLOMMEN)}
                  >
                    <span className="truncate font-mono text-xs text-muted-foreground">{q.quote_number}</span>
                    <span className="flex min-w-0 flex-col">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{q.title}</span>
                        {sig?.soort === 'verloopt' && <span className="lg:hidden"><SignaalLabel signaal={sig} /></span>}
                      </span>
                      <Meta q={q} />
                    </span>
                    <span className="hidden sm:block"><StatusLabel weergave={w} /></span>
                    <span className="hidden text-right tabular-nums md:block">{euro(q.total_amount)}</span>
                    <span className="hidden text-right text-muted-foreground lg:block" title={sinds ?? undefined}>
                      {sinds ? duur(sinds, vandaag) : q.decided_on ? datum(q.decided_on) : '—'}
                    </span>
                    <span className="hidden items-center gap-2 lg:flex">
                      <span className={cn('tabular-nums', sig?.soort === 'verlopen' ? 'font-medium text-red-700' : 'text-muted-foreground')}>{datum(q.valid_until)}</span>
                      {sig?.soort === 'verloopt' && <SignaalLabel signaal={sig} kort />}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            {zichtbaar.length} offerte{zichtbaar.length === 1 ? '' : 's'} · <kbd className="font-mono">/</kbd> zoeken · <kbd className="font-mono">↑ ↓</kbd> bladeren · <kbd className="font-mono">Enter</kbd> openen
          </p>
        </div>
      )}
    </div>
  );
}
